import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { fetchPeople, type Person } from '@/features/people/api'
import { deleteWorkItem, updateWorkItem, type WorkItemChanges } from '@/features/work-items/api'
import type { ApiError } from '@/lib/api/client'
import type { Priority } from '@/types/domain'

const PRIORITIES: Priority[] = ['urgent', 'high', 'normal', 'low']

/**
 * Act on a selection.
 *
 * There is no bulk endpoint: this sends one PATCH per item. That is the right
 * trade at the scale a person actually selects by hand — twenty rows, twenty
 * requests, one activity row each, and every one of them passes the same
 * policy and transition guard a single edit does. A bulk endpoint would have to
 * re-implement all of that to save a few hundred milliseconds.
 *
 * Requests go out together and the failures are counted rather than thrown:
 * closing the bar because one of twenty items was moved by someone else in the
 * meantime would lose the other nineteen.
 */
export function BulkActions({ ids, onClear }: { ids: string[]; onClear: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const people = useQuery<Person[], ApiError>({
    queryKey: ['people'],
    queryFn: fetchPeople,
    retry: false,
  })

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['work-items'] })
    void queryClient.invalidateQueries({ queryKey: ['board'] })
    void queryClient.invalidateQueries({ queryKey: ['timeline'] })
    void queryClient.invalidateQueries({ queryKey: ['activities'] })
  }

  const apply = useMutation<number, ApiError, WorkItemChanges>({
    mutationFn: async (changes) => {
      const results = await Promise.allSettled(ids.map((id) => updateWorkItem(id, changes)))

      return results.filter((result) => result.status === 'rejected').length
    },
    onSuccess: (failed) => {
      refresh()
      if (failed === 0) onClear()
    },
  })

  const remove = useMutation<number, ApiError, void>({
    mutationFn: async () => {
      const results = await Promise.allSettled(ids.map((id) => deleteWorkItem(id)))

      return results.filter((result) => result.status === 'rejected').length
    },
    onSuccess: (failed) => {
      refresh()
      if (failed === 0) onClear()
    },
  })

  const busy = apply.isPending || remove.isPending
  const failed = (apply.data ?? 0) + (remove.data ?? 0)

  return (
    <div className="border-line bg-surface-2 flex flex-wrap items-center gap-2 border-b px-4 py-2">
      <span className="text-ink-900 text-[13px] font-medium">
        {t('work.selectedCount', { count: ids.length })}
      </span>

      <select
        value=""
        disabled={busy || people.data === undefined}
        aria-label={t('work.assignTo')}
        onChange={(event) => {
          const value = event.target.value
          apply.mutate({ assigneeId: value === 'none' ? null : value })
        }}
        className="border-line bg-surface text-ink-700 ms-2 rounded border px-2 py-1 text-[12px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
      >
        <option value="" disabled>
          {t('work.assignTo')}
        </option>
        <option value="none">{t('work.unassigned')}</option>
        {(people.data ?? []).map((person) => (
          <option key={person.id} value={person.id}>
            {person.name}
          </option>
        ))}
      </select>

      <select
        value=""
        disabled={busy}
        aria-label={t('work.setPriority')}
        onChange={(event) => apply.mutate({ priority: event.target.value as Priority })}
        className="border-line bg-surface text-ink-700 rounded border px-2 py-1 text-[12px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
      >
        <option value="" disabled>
          {t('work.setPriority')}
        </option>
        {PRIORITIES.map((priority) => (
          <option key={priority} value={priority}>
            {t(`priority.${priority}`)}
          </option>
        ))}
      </select>

      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (window.confirm(t('work.deleteSelectedConfirm', { count: ids.length }))) {
            remove.mutate()
          }
        }}
        className="text-ink-500 hover:text-[color:var(--color-state-blocked)] flex items-center gap-1.5 text-[12px] disabled:opacity-40"
      >
        <Icon name="trash" className="size-3" />
        {t('view.delete')}
      </button>

      <button
        type="button"
        onClick={onClear}
        className="text-ink-500 hover:text-ink-900 ms-auto text-[12px]"
      >
        {t('work.clearSelection')}
      </button>

      {failed > 0 ? (
        <p className="w-full text-[12px] text-[color:var(--color-state-blocked)]">
          {t('work.bulkPartialFailure', { count: failed })}
        </p>
      ) : null}
    </div>
  )
}
